import React, { useState, useEffect, useMemo, useRef } from "react";
import PortalDropdown from "../components/PortalDropdown";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import moment from "moment-timezone";

// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
import Loader from "../components/Loader";
import DateRangeFilter from "../components/DateRangeFilter";
import { formatDateTimeDisplay, lastSevenDays } from "../utils/common";

// --- Redux ---
import { deployementsListsApis } from "../redux/deployements/deployementsActions";
import {
  selectDeployementsLists,
  selectDeployementsListsStatus,
} from "../redux/deployements/deployementsSelectors";

import {
  createStatusTrackerApis,
  updateDevelopmentTasksApis,
} from "../redux/tasks/taskActions";
import PageHeader from "../components/PageHeader";
import TaskImages from "../components/TaskImages";
import { useTabRefresh } from "../context/TabsContext";

// --------------------------------------------------------------------------
// HELPER FUNCTIONS
// --------------------------------------------------------------------------




const formatDateForPayload = (dateInput) => {
  if (!dateInput) return null;
  return moment(dateInput).format("YYYY-MM-DDTHH:mm:ss");
};

const isValidText = (text) => {
  if (!text) return false;
  const trimmedText = text.trim();
  if (trimmedText.length < 10) return false;
  if (!/[a-zA-Z]/.test(trimmedText)) return false;
  if (!/[aeiouAEIOU]/.test(trimmedText)) return false;
  if (/(.)\1{4,}/.test(trimmedText)) return false;
  if (/[^\s]{15,}/.test(trimmedText)) return false;
  if (/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]{6,}/.test(trimmedText)) return false;
  if (/\b(\w+)\s+\1\s+\1\b/i.test(trimmedText)) return false;
  if (/^([a-zA-Z0-9]\s+){4,}[a-zA-Z0-9]$/.test(trimmedText)) return false;
  return true;
};

// ==========================================
// 1. REUSABLE PORTAL DROPDOWN HELPER
// ==========================================
/* Was a local copy of PortalDropdown, and it had drifted: it took a
   fixed {top, left} computed once on click, listened to nothing, and so
   stayed hanging in the page while the row it belonged to scrolled away.
   The shared component follows its trigger when given an anchorRef and
   closes when it cannot. One copy means one behaviour on every grid. */

// ==========================================
// 2. IS UPDATE SELECT DROPDOWN
// ==========================================
const IsUpdateSelect = ({ initialValue, onChange, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || "None");
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue || "None"), [initialValue]);

  const getColorMap = (val) => {
    const v = String(val || "").toLowerCase();
    if (v === "backend work") return { bg: "#f3e5f5", text: "#7b1fa2" };
    if (v === "update") return { bg: "#e8f5e9", text: "#2e7d32" };
    if (v === "not update") return { bg: "#fff3e0", text: "#e65100" };
    return { bg: "#f8f9fa", text: "#6c757d" };
  };
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuWidth = 150;
      let top = rect.bottom + 5;
      if (viewportHeight - rect.bottom < 150) top = rect.top - 120;

      // Keep the menu inside the viewport's right edge.
      let left = rect.left;
      if (left + menuWidth > viewportWidth - 8) left = rect.right - menuWidth;
      if (left < 8) left = 8;
      setCoords({ top, left });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef} type="button" onClick={handleToggle}
        style={{
          backgroundColor: colors.bg, color: colors.text, border: "none", fontSize: "0.75rem",
          fontWeight: "600", padding: "6px 12px", borderRadius: "12px", display: "inline-flex",
          alignItems: "center", gap: "8px", cursor: readOnly ? "default" : "pointer", whiteSpace: "nowrap",
        }}
      >
        {value} {!readOnly && <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>}
      </button>

      <PortalDropdown isOpen={isOpen} onClose={() => setIsOpen(false)} position={coords}>
        <div className="p-2">
          {["Backend work", "Update", "Not update"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                setValue(option);
                onChange(option);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{ cursor: "pointer", fontSize: "0.85rem", backgroundColor: value === option ? "#f8f9fa" : "transparent" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = value === option ? "#f8f9fa" : "transparent")}
            >
              <i className="bi bi-circle-fill" style={{ fontSize: "0.5rem", color: getColorMap(option).text }}></i>
              <span className="text-dark">{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// ==========================================
// 3. MODERN CLIENT FILTER DROPDOWN
// ==========================================
const ClientFilterDropdown = ({ value, options, onChange, count }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="position-relative" ref={wrapperRef}>
      <div
        onClick={handleToggle}
        className="d-flex align-items-center gap-2 bg-white px-3 py-1 rounded border shadow-sm"
        style={{ cursor: "pointer", transition: "all 0.2s", userSelect: "none" }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#fff"}
      >
        <span className="text-muted small fw-medium" style={{ fontSize: "0.8rem" }}>Tasks:</span>
        <div className="fw-bold text-primary d-flex align-items-center gap-1" style={{ fontSize: "0.85rem" }}>
          <span 
            title={value === "All" ? "All Clients" : value} 
            style={{ 
              display: "inline-block", 
              maxWidth: "200px", 
              whiteSpace: "nowrap", 
              overflow: "hidden", 
              textOverflow: "ellipsis",
              verticalAlign: "bottom"
            }}
          >
            {value === "All" ? "All Clients" : value}
          </span>
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} style={{ fontSize: "0.7rem", marginLeft: "4px", marginTop: "2px" }}></i>
        </div>
        <span
          className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: "24px", height: "24px", fontSize: "0.75rem", marginLeft: "4px" }}
        >
          {count}
        </span>
      </div>

      {isOpen && (
        <div 
          className="position-absolute shadow-lg bg-white p-2" 
          style={{ 
            top: "calc(100% + 8px)", right: 0, borderRadius: "8px", zIndex: 1050, 
            border: "1px solid rgba(0,0,0,0.08)", maxHeight: "300px", overflowY: "auto", 
            minWidth: "260px", maxWidth: "350px", animation: "fadeIn 0.1s ease-out"
          }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); onChange("All"); setIsOpen(false); }}
            className="d-flex align-items-center gap-2 rounded-2 px-3 py-2 mb-1"
            style={{ cursor: "pointer", fontSize: "0.85rem", backgroundColor: value === "All" ? "#f0f7ff" : "transparent" }}
            onMouseEnter={(e) => { if(value !== "All") e.currentTarget.style.backgroundColor = "#f8f9fa" }}
            onMouseLeave={(e) => { if(value !== "All") e.currentTarget.style.backgroundColor = "transparent" }}
          >
            <span className={value === "All" ? "fw-bold text-primary" : "text-dark"}>All Clients</span>
          </div>
          {options.map((client) => (
            <div
              key={client}
              title={client}
              onClick={(e) => { e.stopPropagation(); onChange(client); setIsOpen(false); }}
              className="d-flex align-items-center gap-2 rounded-2 px-3 py-2 mb-1"
              style={{ cursor: "pointer", fontSize: "0.85rem", backgroundColor: value === client ? "#f0f7ff" : "transparent" }}
              onMouseEnter={(e) => { if(value !== client) e.currentTarget.style.backgroundColor = "#f8f9fa" }}
              onMouseLeave={(e) => { if(value !== client) e.currentTarget.style.backgroundColor = "transparent" }}
            >
              <span 
                className={value === client ? "fw-bold text-primary" : "text-dark"}
                style={{ display: "inline-block", maxWidth: "280px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {client}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// --------------------------------------------------------------------------
// UI COMPONENTS
// --------------------------------------------------------------------------

const StatusSelect = ({ currentStatus, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);

  // Add "Deployment" mapping just in case backend returns it directly
  const getColorParams = (status) => {
    switch (status) {
      case "Deployment":
      case "Completed":
      case "Complete":
        return { bg: "#e8f5e9", text: "#2e7d32", dot: "#2e7d32" };
      case "Update":
      case "Testing": // Keeping testing color as fallback for old data
        return { bg: "#e3f2fd", text: "#0d6efd", dot: "#0d6efd" };
      case "Reassign":
        return { bg: "#ffebee", text: "#d32f2f", dot: "#d32f2f" };
      default:
        return { bg: "#f8f9fa", text: "#6c757d", dot: "#adb5bd" };
    }
  };

  const currentStyle = getColorParams(currentStatus || "Select Status");
  
  // MAP VISUAL DISPLAY: Even if backend says 'Deployment', UI says 'Complete'
  const displayStatus = (currentStatus === "Deployment" || currentStatus === "Complete") ? "Complete" : (currentStatus || "-");

  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener("click", handleClose);
      window.addEventListener("scroll", handleClose, true);
      document.addEventListener("close-dropdowns", handleClose);
    }
    return () => {
      window.removeEventListener("click", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      document.removeEventListener("close-dropdowns", handleClose);
    };
  }, [isOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (readOnly) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    document.dispatchEvent(new CustomEvent("close-dropdowns"));

    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const width = Math.max(rect.width, 160);

      // Status is the last column — right-align when a left-anchored menu would
      // overflow the viewport, and never let it slip past either edge.
      let left = rect.left;
      if (left + width > viewportWidth - 8) left = rect.right - width;
      if (left < 8) left = 8;

      // --- COLLISION DETECTION LOGIC ---
      const spaceBelow = viewportHeight - rect.bottom;
      const estimatedDropdownHeight = 130;

      let dynamicStyle = {
        position: "fixed",
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 10000, 
        backgroundColor: "#fff", 
        borderRadius: "12px",
      };

      if (spaceBelow < estimatedDropdownHeight) {
        dynamicStyle.bottom = `${viewportHeight - rect.top + 5}px`;
      } else {
        dynamicStyle.top = `${rect.bottom + 5}px`;
      }

      setMenuStyle(dynamicStyle);
      setIsOpen(true);
    }
  };

  if (readOnly) {
    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{
          backgroundColor: currentStyle.bg, color: currentStyle.text, padding: "6px 14px",
          borderRadius: "20px", fontSize: "0.75rem", fontWeight: "600", width: "fit-content", margin: "0 auto",
        }}
      >
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: currentStyle.dot }}></div>
        <span>{displayStatus}</span>
      </div>
    );
  }

  return (
    <>
      <div ref={wrapperRef} className="position-relative d-inline-block">
        <button
          type="button" onClick={toggleMenu}
          className="d-flex align-items-center gap-2 shadow-sm border-0"
          style={{
            backgroundColor: currentStyle.bg, color: currentStyle.text, padding: "6px 14px",
            borderRadius: "20px", fontSize: "0.75rem", fontWeight: "600", cursor: "pointer",
            minWidth: "125px", justifyContent: "space-between",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: currentStyle.dot }}></div>
            <span>{displayStatus}</span>
          </div>
          <i className="bi bi-chevron-down" style={{ fontSize: "0.6rem", opacity: 0.7 }}></i>
        </button>
      </div>

      {isOpen && (
        <div className="shadow-lg p-1 border-0 overflow-auto" style={menuStyle}>
          {["Update", "Complete", "Reassign"].map((opt) => {
            const optStyle = getColorParams(opt);

            // --- THE MAGIC HAPPENS HERE: If they pick 'Complete', we actually send 'Deployment' to parent ---
            const actualValue = opt === "Complete" ? "Deployment" : opt;
            const isSelected = actualValue === currentStatus || (opt === "Complete" && (currentStatus === "Complete" || currentStatus === "Deployment"));

            return (
              <div
                key={opt}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelected) {
                    onChange(actualValue); // Sends "Deployment" to handleStatusChange
                    setIsOpen(false);
                  }
                }}
                className="d-flex align-items-center px-3 py-2 rounded-2 my-1"
                style={{
                  cursor: isSelected ? "default" : "pointer", fontSize: "0.8rem", color: "#495057",
                  backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                }}
              >
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: optStyle.dot, marginRight: "10px" }}></div>
                <span className="flex-grow-1">{opt}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const DescriptionCell = ({ value, onEdit, readOnly }) => {
  const truncateText = (str) => (str && str.length > 20 ? str.substring(0, 20) + "..." : str);
  if (readOnly)
    return <span className="text-muted small" title={value}>{truncateText(value) || "-"}</span>;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onEdit(); }}
      className="text-truncate text-muted d-flex align-items-center"
      title={value}
      style={{ cursor: "pointer", maxWidth: "200px", fontSize: "0.85rem" }}
    >
      <span className="me-2">{value ? truncateText(value) : "Add Description..."}</span>
      <i className="bi bi-pencil text-primary" style={{ fontSize: "0.7rem", opacity: 0.7 }}></i>
    </div>
  );
};

const TaskDetailContent = ({ task }) => {
  if (!task) return null;
  const DetailRow = ({ label, value, fullWidth = false, isBadge = false, badgeColor = "secondary" }) => (
    <div className={`mb-3 ${fullWidth ? "col-12" : "col-md-4 col-sm-6"}`}>
      <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>{label}</div>
      {isBadge ? (
        <span className={`badge bg-${badgeColor} text-${badgeColor === "warning" || badgeColor === "info" ? "dark" : "white"}`}>{value}</span>
      ) : (
        <div className="fw-medium text-dark" style={{ fontSize: "0.9rem", wordBreak: "break-word" }}>{value || "-"}</div>
      )}
    </div>
  );

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-primary mb-1">{task.TaskName}</h5>
          <div className="text-muted small">{task.NatureofQuery}</div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          <span className="badge bg-light text-secondary border">Query #{task.QueryNo}</span>
          <small className="text-muted">
            {formatDateTimeDisplay(task.QueryDate)}
          </small>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-12"><h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">Client Information</h6></div>
        <DetailRow label="Client / Company" value={task.ClientName || task.CompanyName} fullWidth />
        <DetailRow label="Project Name" value={task.ProjectName} />
        <DetailRow label="User Name" value={task.Client_UserName} />
        <DetailRow label="Mobile No" value={task.MobileNo} />
        
        <div className="col-12 mt-2"><h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">Task Settings</h6></div>
        <DetailRow label="Status" value={(task.Status === "Deployment" || task.Status === "Complete") ? "Complete" : task.Status} isBadge badgeColor={(task.Status === "Deployment" || task.Status === "Complete") ? "success" : "primary"} />
        <DetailRow label="Priority" value={task.Priority} isBadge badgeColor={task.Priority === "High" ? "danger" : "info"} />
        <DetailRow label="Assigned Time" value={formatDateTimeDisplay(task.AssignedTime)} />
        
        <div className="col-12 mt-2"><h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">Timing</h6></div>
        <DetailRow label="Est. Time" value={task.EstimateTime ? `${task.EstimateTime} mins` : "-"} />
        <DetailRow label="Start Time" value={formatDateTimeDisplay(task.StartTime)} />
        <DetailRow label="End Time" value={formatDateTimeDisplay(task.EndTime)} />
        
        <div className="col-12 mt-2"><h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">Content</h6></div>
        <DetailRow label="Description" value={task.Description} fullWidth />

        {/* Same block as the other task dialogs, read-only: the screenshot is
            part of the description, and it should not matter which screen you
            opened the task from. */}
        {task.QueryCode && (
          <div className="col-12 mt-2">
            <TaskImages queryCode={task.QueryCode} readOnly />
          </div>
        )}
        <DetailRow label="Solution" value={task.Solution} fullWidth />
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------------------------------

const Deployments = () => {
  const dispatch = useDispatch();
  const [viewFilter, setViewFilter] = useState("Incomplete");
  /* { start, end } — same keys handleDateChange writes and every reader uses.
     Seeded as { startDate, endDate }, the default was invisible to the picker
     and the first fetch went out with no dates. */
  const [dateRange, setDateRange] = useState(lastSevenDays);

  const [localTasks, setLocalTasks] = useState([]);

  // --- CLIENT COUNT & FILTER LOGIC ---
  const [selectedClientFilter, setSelectedClientFilter] = useState("All");

  const uniqueClients = useMemo(() => {
    const clients = localTasks.map((t) => t.ClientName || t.CompanyName || "Unknown");
    return [...new Set(clients)].filter(Boolean);
  }, [localTasks]);

  const filteredTasks = useMemo(() => {
    if (selectedClientFilter === "All") return localTasks;
    return localTasks.filter(
      (t) => (t.ClientName || t.CompanyName || "Unknown") === selectedClientFilter
    );
  }, [localTasks, selectedClientFilter]);

  const displayTaskCount = filteredTasks.length;  

  // --- MODAL & ACTION STATES ---
  const [editDescTask, setEditDescTask] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descText, setDescText] = useState("");
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tasksData = useSelector(selectDeployementsLists);
  const listStatus = useSelector(selectDeployementsListsStatus);

  const lastRequestRef = useRef(null);

  const fetchTasks = () => {
    const apiParams = {
      isManuallyRaised: null,
      currentPage,
      pageSize,
      edit: viewFilter === "Complete" ? 1 : 0,
      fromDate: dateRange.start && viewFilter !== "Incomplete" ? format(dateRange.start, "yyyy-MM-dd") : null,
      toDate: dateRange.end && viewFilter !== "Incomplete" ? format(dateRange.end, "yyyy-MM-dd") : null,
    };

    // Skip duplicate calls (StrictMode double-invoke + re-renders with same params).
    const requestKey = JSON.stringify(apiParams);
    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    dispatch(deployementsListsApis(apiParams));
  };

  /* Coming back to this tab re-asks for the list. The de-dupe above is keyed
     on the parameters, which have not changed — so it is cleared first, or
     the call would be swallowed as a repeat of itself. */
  useTabRefresh(() => {
    lastRequestRef.current = null;
    fetchTasks();
  });

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, viewFilter, dateRange]);

  useEffect(() => {
    const rawData = tasksData?.data;
    const list = Array.isArray(rawData) ? rawData : rawData?.data || [];
    if (list) {
      setLocalTasks(
        list.map((task) => ({
          ...task,
          totalTimeMs: Number(task?.totalTimeMs) || 0,
          currentSessionStart: task?.currentSessionStart || null,
          Status: task?.Status || "Update", // Defaulting based on UI prompt
        })),
      );
    }
  }, [tasksData]);

  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
    if ((!start && !end) || (start && end)) setCurrentPage(1);
  };

  const handleExport = (data) => alert(`Exporting ${data.length} tasks...`);

  // --- BACKEND SYNC ---
  const syncWithBackend = (task, type) => {
    setLoading(true);
    let payload = { queryCode: task.QueryCode };

    if (task?.Status === "Update" || task?.Status === "Reassign") {
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.status = task.Status;
      payload.description = task.Description;
      payload.isUpdate = task.isUpdate;
    } else if (task?.Status === "Deployment" || task?.Status === "Complete") {
      payload.endTime = formatDateForPayload(task.EndTime);
      payload.status = "Deployment"; // FORCE "Deployment" string to backend
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.description = task.Description;
      payload.timeTaken = task.TotalTimeTaken_Seconds;
      payload.isUpdate = task.isUpdate;
    } else if (type === "DESCRIPTION") {
      payload.description = task.Description || "";
      payload.status = task?.Status;
      payload.isUpdate = task.isUpdate;
    } else {
      payload.startTime = null;
      payload.endTime = null;
      payload.status = task?.Status;
      payload.isUpdate = task.isUpdate;
    }

    const statusTrackerPayload = {
      queryCode: task.QueryCode,
      status: (task?.Status === "Deployment" || task?.Status === "Complete") ? "Deployment" : task?.Status,
    };

    if (type === "DESCRIPTION" && task.Status !== "Deployment" && task.Status !== "Complete") {
      dispatch(updateDevelopmentTasksApis(payload)).then(() => {
        setLoading(false);
        fetchTasks();
      });
    } else {
      dispatch(createStatusTrackerApis(statusTrackerPayload)).then((value) => {
        if (value?.payload?.success) {
          if (task?.Status === "Deployment" || task?.Status === "Complete") {
            payload.timeTaken = value?.payload?.data?.[0]?.TotalTimeTaken_Seconds;
          }
          dispatch(updateDevelopmentTasksApis(payload)).then(() => {
            setLoading(false);
            fetchTasks();
          });
        } else {
          setLoading(false);
        }
      });
    }
  };

  // --- HANDLE STATUS CHANGE ---
  const handleStatusChange = (rowId, newStatus) => {
    const taskIndex = localTasks.findIndex((t) => t.QueryNo === rowId);
    if (taskIndex === -1) return;
    const task = localTasks[taskIndex];

    const currentIsUpdate = task.IsUpdate || task.isUpdate;
    if (!currentIsUpdate || currentIsUpdate === "None" || currentIsUpdate === "null") {
      toast.warning("Please select a valid 'Is Update' option before updating the status.");
      return;
    }

    // Since we intercept 'Complete' to map to 'Deployment', check against 'Deployment' here
    if (newStatus === "Deployment" && (!task.Description || task.Description.trim() === "")) {
      toast.warning("Description is required to complete the deployment.");
      setPendingStatusUpdate({ rowId, newStatus });
      openDescriptionModal(task);
      return;
    }

    const now = new Date();
    let updates = { Status: newStatus };

    if ((newStatus === "Update" || newStatus === "Reassign") && !task.StartTime) {
      updates.StartTime = now.toISOString();
    }
    if (newStatus === "Deployment") {
      updates.EndTime = now.toISOString();
    }

    const updatedTask = { ...task, ...updates };

    setLocalTasks((prev) => {
      const isTerminal = newStatus === "Deployment" || newStatus === "Complete";
      if (viewFilter === "Incomplete" && isTerminal) return prev.filter((t) => t.QueryNo !== rowId);
      if (viewFilter === "Complete" && !isTerminal) return prev.filter((t) => t.QueryNo !== rowId);
      return prev.map((t) => (t.QueryNo === rowId ? updatedTask : t));
    });

    syncWithBackend(updatedTask);
  };

  const handleIsUpdateChange = async (task, newValue) => {
    setLoading(true);
    setLocalTasks((prev) =>
      prev.map((t) => (t.QueryNo === task.QueryNo ? { ...t, IsUpdate: newValue, isUpdate: newValue } : t)),
    );

    const payload = {
      queryCode: task.QueryCode || task.QueryNo,
      isUpdate: newValue,
      status: task.Status || "Update",
    };

    try {
      const res = await dispatch(updateDevelopmentTasksApis(payload));
      if (res?.payload?.success) {
        toast.success("Is Update status changed!");
        fetchTasks();
      } else {
        toast.error("Failed to update status");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to update status");
      fetchTasks();
    } finally {
      setLoading(false);
    }
  };

  const openDescriptionModal = (task) => {
    setEditDescTask(task);
    setDescText(task.Description || "");
    setShowDescModal(true);
  };

  const handleSaveDescription = () => {
    if (!editDescTask) return;
    if (!descText || descText.trim() === "") {
      toast.error("Description cannot be empty. Please enter a description.");
      return;
    }

    const now = new Date();
    let updates = { Description: descText.trim() };

    if (pendingStatusUpdate && pendingStatusUpdate.rowId === editDescTask.QueryNo) {
      updates.Status = pendingStatusUpdate.newStatus;
      if (updates.Status === "Deployment") {
        updates.EndTime = now.toISOString();
      }
    }

    const updatedTask = { ...editDescTask, ...updates };

    setLocalTasks((prev) => {
      const isTerminalState = updatedTask.Status === "Deployment" || updatedTask.Status === "Complete";
      if (viewFilter === "Incomplete" && isTerminalState) {
        return prev.filter((t) => t.QueryNo !== updatedTask.QueryNo);
      }
      return prev.map((t) => (t.QueryNo === updatedTask.QueryNo ? updatedTask : t));
    });

    if (pendingStatusUpdate) syncWithBackend(updatedTask);
    else syncWithBackend(updatedTask, "DESCRIPTION");

    setShowDescModal(false);
    setPendingStatusUpdate(null);
  };

  const columns = useMemo(() => {
    const commonCols = [
      {
        /* Was key: "UserName" — a column labelled Client Name, filtering on a
           field the rows do not carry. Same fix as DevelopmentTask: the key
           names what is shown, and searchValue/sortValue spell out the same
           CompanyName fallback the cell renders. */
        label: "Client Name", key: "ClientName", width: "130px", cellClass: "text-cell-wrap",
        searchValue: (r) => r.ClientName || r.CompanyName || "",
        sortValue: (r) => r.ClientName || r.CompanyName || "",
        render: (r) => <div style={{ wordBreak: "break-word", whiteSpace: "normal", overflow: "hidden" }}>{r.ClientName || r.CompanyName || "-"}</div>,
      },
      {
        label: "Form Name", key: "FormName", width: "180px", cellClass: "text-cell-wrap",
        /* The cell falls back to MainMenuName, so the filter has to too. */
        searchValue: (r) => r.FormName || r.MainMenuName || "",
        sortValue: (r) => r.FormName || r.MainMenuName || "",
        render: (r) => <div style={{ wordBreak: "break-all", whiteSpace: "normal", overflow: "hidden" }}>{r.FormName || r.MainMenuName || "-"}</div>,
      },
      {
        label: "Task Name", key: "TaskName", width: "250px", cellClass: "text-cell-wrap",
        /* A task with no TaskName shows its NatureofQuery; searching the words
           on screen has to find it. */
        searchValue: (r) => r.TaskName || r.NatureofQuery || "",
        sortValue: (r) => r.TaskName || r.NatureofQuery || "",
        render: (r) => (
          <div onClick={() => { setSelectedTask(r); setShowDetailModal(true); }} className="text-primary fw-medium cursor-pointer" style={{ cursor: "pointer", wordBreak: "break-word", whiteSpace: "normal", overflow: "hidden" }}>
            {r.TaskName || r.NatureofQuery}
          </div>
        ),
      },
      {
        label: "Query Type", key: "QueryType", width: "150px",
        render: (r) => {
          let badgeClass = "bg-secondary";
          const type = r.QueryType ? r.QueryType.trim() : "";
          if (type === "Bug" || type === "BUG") badgeClass = "bg-danger";
          else if (type === "New Task") badgeClass = "bg-primary";
          else if (type === "Rework") badgeClass = "bg-warning text-dark";

          return <span className={`badge ${badgeClass} rounded-pill`} style={{ padding: "6px 12px", letterSpacing: "0.5px" }}>{type || "-"}</span>;
        },
      },
      {
        label: "Start Time", key: "StartTime", type: "datetime", width: "140px",
        render: (r) => (
          <span className="text-muted small">
            {formatDateTimeDisplay(r.StartTime)}
          </span>
        ),
      },
      {
        label: "Is Update", key: "IsUpdate", width: "140px", type: "select",
        render: (r) => (
          <IsUpdateSelect initialValue={r.IsUpdate || r.isUpdate} onChange={(val) => handleIsUpdateChange(r, val)} readOnly={viewFilter === "Complete"} />
        ),
      },
    ];

    const descriptionCol = {
      label: "Description", key: "Description", width: "200px",
      render: (r) => <DescriptionCell value={r.Description} onEdit={() => openDescriptionModal(r)} readOnly={viewFilter === "Complete"} />,
    };

    const statusCol = {
      label: "Status", key: "Status", width: "160px",
      render: (r) => (
        <StatusSelect currentStatus={r.Status} readOnly={viewFilter === "Complete"} onChange={(val) => handleStatusChange(r.QueryNo, val)} />
      ),
    };

    if (viewFilter === "Incomplete") {
      return [...commonCols, descriptionCol, statusCol];
    } else {
      return [
        ...commonCols,
        {
          label: "End Time", key: "EndTime", type: "datetime", width: "140px",
          render: (r) => (
            <span className="text-muted small">
              {formatDateTimeDisplay(r.EndTime)}
            </span>
          ),
        },
        descriptionCol,
        statusCol,
      ];
    }
  }, [viewFilter, localTasks]);

  return (
    <div className="report-container">
   <PageHeader
  title={
    <div className="d-flex align-items-center text-nowrap">
      <i className="bi-server me-2 text-primary"></i>
      <span>Deployments</span>
    </div>
  }
  subtitle="Manage and Monitor Application Deployment"
   actions={
        <ClientFilterDropdown
          value={selectedClientFilter}
          options={uniqueClients}
          onChange={setSelectedClientFilter}
          count={displayTaskCount}
        />
      }
 />
    

      <div style={{ position: "relative", minHeight: "200px" }}>
        {(listStatus === "loading" || loading) && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255, 255, 255, 0.6)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader isLoading={true} />
          </div>
        )}
        <ReportTable data={filteredTasks} columns={columns} onExport={handleExport}   stickyColumns={[]} />
      </div>

      <Modal show={showDescModal} onClose={() => { setShowDescModal(false); setPendingStatusUpdate(null); }} title="Edit Description" maxWidth="500px">
        <div className="p-3">
          <textarea className="form-control mb-3" rows="5" value={descText} onChange={(e) => setDescText(e.target.value)} autoFocus />
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-sm btn-light border" onClick={() => { setShowDescModal(false); setPendingStatusUpdate(null); }}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSaveDescription}>Save</button>
          </div>
        </div>
      </Modal>

      <Modal show={showDetailModal} onClose={() => setShowDetailModal(false)} title="Task Details" maxWidth="900px">
        {selectedTask && <TaskDetailContent task={selectedTask} />}
      </Modal>
    </div>
  );
};

export default Deployments;
